import { z } from 'zod';
import { AdvisorPhoneSchema, AppointmentOutcomeSchema, AppointmentOutcomeSourceSchema } from './appointment-outcome.js';
import { CaseAlertSchema, HomeProjectSummarySchema } from './api-supervision.js';
import { HandoffAssignmentSourceSchema, OpportunityControlSchema, OpportunityControlStateSchema } from './api-handoff.js';
import {
  AnalysisStatusSchema,
  AppointmentStatusSchema,
  CalendlyScopeSchema,
  CallStatusSchema,
  ChannelKindSchema,
  ConnectionProviderSchema,
  ConnectionStatusSchema,
  ConversationModeSchema,
  DeliveryStatusSchema,
  DeploymentStatusSchema,
  DirectionSchema,
  DocumentStatusSchema,
  EndpointKindSchema,
  HandoffStatusSchema,
  InventoryAuthoritySchema,
  MessageAuthorSchema,
  PermissionStatusSchema,
  PlatformCapabilitySchema,
  ProjectStatusSchema,
  ReceiptStatusSchema,
  RoleSchema,
  SetupStepKeySchema,
  SetupStepStatusSchema,
  UnitAvailabilitySchema,
  VersionStateSchema,
  WebhookDeliveryStatusSchema,
  WebhookEventTypeSchema,
  WhatsAppModeSchema,
} from './enums.js';
import { AgentPromptContentSchema, GlobalPromptContentSchema, ToolInstructionContentSchema } from './prompts.js';
import { FunnelStageInputSchema, FunnelStageSchema, LossReasonCategorySchema, QualificationValueStatusSchema } from './api-funnel.js';
import { ContactWindowSchema } from './schedule.js';
import { FrequencyPolicySchema } from './cadence.js';
import { ProjectActivationStateSchema } from './launch.js';

// All timestamps on the wire are ISO-8601 UTC strings. Money is { amountMinor: string, currency }.
// null means "unknown / not provided" and must never be rendered as zero.

const Iso = z.string();
const Id = z.string().min(1).max(64);
const Email = z.email().max(254);

export const ErrorResponseSchema = z.object({
  error: z.object({ code: z.string(), message: z.string(), details: z.unknown().optional() }),
});
export type ErrorResponse = z.infer<typeof ErrorResponseSchema>;

export const OkSchema = z.object({ ok: z.literal(true) });

export function pageOf<T extends z.ZodType>(item: T) {
  return z.object({ items: z.array(item), nextCursor: z.string().nullable() });
}
export const PageQuerySchema = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});

export const MoneySchema = z.object({ amountMinor: z.string().regex(/^\d+$/), currency: z.string().length(3) });
export type Money = z.infer<typeof MoneySchema>;

// ───────────── Auth ─────────────

/** Password policy: length over composition rules (NIST 800-63B). */
export const PasswordSchema = z.string().min(12, 'Usa al menos 12 caracteres').max(256);

export const LoginRequestSchema = z.object({ email: Email, password: z.string().min(1).max(256) });
export const LoginResponseSchema = z.object({
  /** true when the account has MFA enrolled and the session must be verified with a TOTP code. */
  mfaRequired: z.boolean(),
  /** true when policy requires enrolling MFA before admin actions. */
  mfaEnrollmentRequired: z.boolean(),
});
export type LoginResponse = z.infer<typeof LoginResponseSchema>;

export const ForgotPasswordRequestSchema = z.object({ email: Email });
export const ResetPasswordRequestSchema = z.object({ token: z.string().min(20).max(200), password: PasswordSchema });
export const VerifyEmailRequestSchema = z.object({ token: z.string().min(20).max(200) });
export const ChangePasswordRequestSchema = z.object({ currentPassword: z.string().min(1).max(256), newPassword: PasswordSchema });

export const InvitationPreviewSchema = z.object({
  workspaceName: z.string(),
  email: z.string(),
  role: RoleSchema,
  expiresAt: Iso,
  /** Whether an account already exists for this email (the UI asks to sign in instead of registering). */
  accountExists: z.boolean(),
});
export const AcceptInvitationRequestSchema = z.object({
  token: z.string().min(20).max(200),
  /** Required only when creating a new account. */
  name: z.string().min(1).max(120).optional(),
  password: PasswordSchema.optional(),
});
export const AcceptInvitationResponseSchema = z.object({ workspaceId: Id, signedIn: z.boolean() });

export const MfaSetupResponseSchema = z.object({ otpauthUrl: z.string(), secret: z.string() });
export const MfaCodeRequestSchema = z.object({ code: z.string().regex(/^\d{6}$/, 'Código de 6 dígitos') });

export const SessionInfoSchema = z.object({
  id: Id,
  current: z.boolean(),
  createdAt: Iso,
  lastSeenAt: Iso,
  ip: z.string().nullable(),
  userAgent: z.string().nullable(),
});

// ───────────── Session / tenancy ─────────────

export const MembershipSummarySchema = z.object({
  workspaceId: Id,
  workspaceName: z.string(),
  workspaceSlug: z.string(),
  role: RoleSchema,
  isDemo: z.boolean(),
  testMode: z.boolean(),
  setupReady: z.number().int(),
  setupTotal: z.number().int(),
  /** Connections needing attention (health signal for the switcher). */
  attentionCount: z.number().int(),
});
export type MembershipSummary = z.infer<typeof MembershipSummarySchema>;

export const MeResponseSchema = z.object({
  user: z.object({
    id: Id,
    email: z.string(),
    name: z.string(),
    emailVerified: z.boolean(),
    mfaEnabled: z.boolean(),
    capabilities: z.array(PlatformCapabilitySchema),
  }),
  session: z.object({ mfaVerified: z.boolean(), mfaEnrollmentRequired: z.boolean() }),
  memberships: z.array(MembershipSummarySchema),
  /** Self-service workspace creation is open to this user (false → invitation-only). */
  canCreateWorkspace: z.boolean().optional(),
});
export type MeResponse = z.infer<typeof MeResponseSchema>;

// ───────────── Workspace ─────────────

export const WorkspaceSchema = z.object({
  id: Id,
  name: z.string(),
  legalName: z.string().nullable(),
  slug: z.string(),
  timezone: z.string(),
  locale: z.string(),
  defaultCountry: z.string(),
  currency: z.string(),
  supportEmail: z.string().nullable(),
  supportPhone: z.string().nullable(),
  website: z.string().nullable(),
  description: z.string().nullable(),
  isDemo: z.boolean(),
  testMode: z.boolean(),
  testAllowlist: z.array(z.string()),
  /** Proactive contact window (spec §15.4), every day in the project's timezone; projects may override it. */
  contactWindow: ContactWindowSchema,
  createdAt: Iso,
});
export type Workspace = z.infer<typeof WorkspaceSchema>;

export const CreateWorkspaceSchema = z.object({
  name: z.string().min(2).max(120),
  timezone: z.string().min(1).max(64).default('America/Mexico_City'),
});
export const UpdateWorkspaceSchema = z
  .object({
    name: z.string().min(2).max(120),
    legalName: z.string().max(200).nullable(),
    timezone: z.string().min(1).max(64),
    locale: z.string().min(2).max(16),
    defaultCountry: z.string().length(2),
    currency: z.string().length(3),
    supportEmail: Email.nullable(),
    supportPhone: z.string().max(40).nullable(),
    website: z.string().max(300).nullable(),
    description: z.string().max(4000).nullable(),
    testMode: z.boolean(),
    testAllowlist: z.array(z.string().max(40)).max(50),
    contactWindow: ContactWindowSchema,
  })
  .partial();

// ───────────── Members & invitations ─────────────

export const MemberSchema = z.object({
  id: Id,
  userId: Id,
  email: z.string(),
  name: z.string(),
  role: RoleSchema,
  restrictedToProjects: z.boolean(),
  projectIds: z.array(Id),
  canAccessRecordings: z.boolean(),
  /** WhatsApp number for booking notices (spec §05.17); null = none registered. */
  whatsappPhone: z.string().nullable().default(null),
  mfaEnabled: z.boolean(),
  lastLoginAt: Iso.nullable(),
  createdAt: Iso,
});
export type Member = z.infer<typeof MemberSchema>;

export const UpdateMemberSchema = z
  .object({
    role: RoleSchema,
    restrictedToProjects: z.boolean(),
    projectIds: z.array(Id).max(500),
    canAccessRecordings: z.boolean(),
    whatsappPhone: AdvisorPhoneSchema.nullable(),
  })
  .partial();

export const InvitationSchema = z.object({
  id: Id,
  email: z.string(),
  role: RoleSchema,
  restrictedToProjects: z.boolean(),
  projectIds: z.array(Id),
  status: z.enum(['pending', 'accepted', 'revoked', 'expired']),
  expiresAt: Iso,
  createdAt: Iso,
  invitedByName: z.string().nullable(),
});
export type Invitation = z.infer<typeof InvitationSchema>;

export const CreateInvitationSchema = z.object({
  email: Email,
  role: RoleSchema,
  restrictedToProjects: z.boolean().default(false),
  projectIds: z.array(Id).max(500).default([]),
});

// ───────────── Setup checklist ─────────────

export const SetupStepSchema = z.object({
  key: SetupStepKeySchema,
  status: SetupStepStatusSchema,
  note: z.string().nullable(),
  /** Server-computed hint (e.g. "Falta conectar Calendly"). Independent of the manual status. */
  hint: z.string().nullable(),
  updatedAt: Iso.nullable(),
});
export type SetupStep = z.infer<typeof SetupStepSchema>;
export const SetupChecklistSchema = z.object({ steps: z.array(SetupStepSchema), ready: z.number().int(), total: z.number().int() });
export const UpdateSetupStepSchema = z.object({ status: SetupStepStatusSchema, note: z.string().max(500).nullable().optional() });

// ───────────── Projects ─────────────

export const ProjectSchema = z.object({
  id: Id,
  name: z.string(),
  externalId: z.string().nullable(),
  status: ProjectStatusSchema,
  city: z.string().nullable(),
  state: z.string().nullable(),
  address: z.string().nullable(),
  locationNotes: z.string().nullable(),
  visitLocation: z.string().nullable(),
  sellingPoints: z.array(z.string()),
  permittedClaims: z.array(z.string()),
  description: z.string().nullable(),
  timezone: z.string().nullable(),
  inventoryAuthority: InventoryAuthoritySchema,
  freshnessLimitSeconds: z.number().int(),
  calendlyScope: CalendlyScopeSchema,
  calendlyConnectionId: Id.nullable(),
  /** Own proactive contact window; null = the workspace's (spec §10.6, §15.4). */
  contactWindow: ContactWindowSchema.nullable(),
  /** Reactive WhatsApp replies at night identifying as AI (default true). false = one consolidated reply at 8:00. */
  nightReplies: z.boolean(),
  /** Contact frequency policy (spec §03.1, §03.2, C1); null = the spec defaults (FREQUENCY_DEFAULTS). */
  frequencyPolicy: FrequencyPolicySchema.nullable(),
  /** Launch gate (spec §10.1–§10.2): real prospects are attended only while `live`. */
  activationState: ProjectActivationStateSchema,
  unitCount: z.number().int(),
  createdAt: Iso,
  updatedAt: Iso,
});
export type Project = z.infer<typeof ProjectSchema>;

export const CreateProjectSchema = z.object({
  name: z.string().min(2).max(160),
  externalId: z.string().max(200).nullable().optional(),
  city: z.string().max(120).nullable().optional(),
  state: z.string().max(120).nullable().optional(),
});
export const UpdateProjectSchema = z
  .object({
    name: z.string().min(2).max(160),
    externalId: z.string().max(200).nullable(),
    status: ProjectStatusSchema,
    city: z.string().max(120).nullable(),
    state: z.string().max(120).nullable(),
    address: z.string().max(400).nullable(),
    locationNotes: z.string().max(2000).nullable(),
    visitLocation: z.string().max(400).nullable(),
    sellingPoints: z.array(z.string().min(1).max(300)).max(30),
    permittedClaims: z.array(z.string().min(1).max(300)).max(30),
    description: z.string().max(6000).nullable(),
    timezone: z.string().max(64).nullable(),
    inventoryAuthority: InventoryAuthoritySchema,
    freshnessLimitSeconds: z.number().int().min(30).max(86_400),
    calendlyScope: CalendlyScopeSchema,
    calendlyConnectionId: Id.nullable(),
    contactWindow: ContactWindowSchema.nullable(),
    nightReplies: z.boolean(),
    frequencyPolicy: FrequencyPolicySchema.nullable(),
  })
  .partial();

// ───────────── Inventory ─────────────

export const UnitSchema = z.object({
  id: Id,
  projectId: Id,
  code: z.string(),
  externalId: z.string().nullable(),
  propertyType: z.string(),
  phase: z.string().nullable(),
  building: z.string().nullable(),
  floor: z.string().nullable(),
  bedrooms: z.number().int().nullable(),
  bathrooms: z.number().nullable(),
  area: z.number().nullable(),
  areaUnit: z.string(),
  price: MoneySchema.nullable(),
  availability: UnitAvailabilitySchema,
  reservationExternalId: z.string().nullable(),
  holdExpiresAt: Iso.nullable(),
  paymentConditions: z.string().nullable(),
  sourceRevision: z.string().nullable(),
  lastVerifiedAt: Iso.nullable(),
  /** fresh | stale | unknown — computed against the project's freshness limit and source authority. */
  freshness: z.enum(['fresh', 'stale', 'unknown']),
  updatedAt: Iso,
});
export type Unit = z.infer<typeof UnitSchema>;

export const UpsertUnitSchema = z.object({
  code: z.string().min(1).max(60),
  propertyType: z.string().min(1).max(60),
  phase: z.string().max(60).nullable().optional(),
  building: z.string().max(60).nullable().optional(),
  floor: z.string().max(20).nullable().optional(),
  bedrooms: z.number().int().min(0).max(20).nullable().optional(),
  bathrooms: z.number().min(0).max(20).nullable().optional(),
  area: z.number().min(0).max(1_000_000).nullable().optional(),
  areaUnit: z.string().max(10).default('m2'),
  priceMinor: z.string().regex(/^\d+$/).nullable().optional(),
  currency: z.string().length(3).default('MXN'),
  availability: UnitAvailabilitySchema.default('unknown'),
  paymentConditions: z.string().max(2000).nullable().optional(),
});
// PATCH semantics: no defaults (zod's .partial() would still apply the create defaults and reset fields).
export const UpdateUnitSchema = z
  .object({
    code: z.string().min(1).max(60),
    propertyType: z.string().min(1).max(60),
    phase: z.string().max(60).nullable(),
    building: z.string().max(60).nullable(),
    floor: z.string().max(20).nullable(),
    bedrooms: z.number().int().min(0).max(20).nullable(),
    bathrooms: z.number().min(0).max(20).nullable(),
    area: z.number().min(0).max(1_000_000).nullable(),
    areaUnit: z.string().max(10),
    priceMinor: z.string().regex(/^\d+$/).nullable(),
    currency: z.string().length(3),
    availability: UnitAvailabilitySchema,
    paymentConditions: z.string().max(2000).nullable(),
  })
  .partial();

// ───────────── Integrations ─────────────

export const CapabilityStateSchema = z.enum(['verified', 'unverified', 'unsupported', 'failed']);
export const CapabilityReportSchema = z.record(z.string(), z.object({ state: CapabilityStateSchema, detail: z.string().nullable() }));
export type CapabilityReport = z.infer<typeof CapabilityReportSchema>;

export const ChannelEndpointSchema = z.object({
  id: Id,
  connectionId: Id,
  kind: EndpointKindSchema,
  displayName: z.string(),
  phoneE164: z.string().nullable(),
  whatsappMode: WhatsAppModeSchema.nullable(),
  shared: z.boolean(),
  status: ConnectionStatusSchema,
  healthMessage: z.string().nullable(),
  projectIds: z.array(Id),
  defaultProjectId: Id.nullable(),
});
export type ChannelEndpoint = z.infer<typeof ChannelEndpointSchema>;

export const ConnectionSchema = z.object({
  id: Id,
  provider: ConnectionProviderSchema,
  label: z.string(),
  status: ConnectionStatusSchema,
  externalAccountId: z.string().nullable(),
  externalAccountName: z.string().nullable(),
  capabilities: CapabilityReportSchema,
  capabilitiesCheckedAt: Iso.nullable(),
  healthMessage: z.string().nullable(),
  lastHealthAt: Iso.nullable(),
  allowedProjectIds: z.array(Id),
  allowedEventTypes: z.array(z.string()),
  hasCredentials: z.boolean(),
  /** Generic webhook ingress URL (absolute) when provider = generic_webhook. */
  ingressUrl: z.string().nullable(),
  endpoints: z.array(ChannelEndpointSchema),
  createdAt: Iso,
});
export type Connection = z.infer<typeof ConnectionSchema>;

/** Which provider flows this deployment can run (platform app registration present, adapter implemented). */
export const ProviderAvailabilitySchema = z.object({
  provider: ConnectionProviderSchema,
  available: z.boolean(),
  methods: z.array(z.enum(['oauth', 'api_token', 'hosted_setup', 'embedded_signup', 'secret'])),
  reason: z.string().nullable(),
});
export type ProviderAvailability = z.infer<typeof ProviderAvailabilitySchema>;

export const CreateWebhookConnectionSchema = z.object({
  label: z.string().min(1).max(120),
  allowedProjectIds: z.array(Id).max(500).default([]),
  allowedEventTypes: z.array(z.string().max(80)).max(50).default([]),
});
export const CreatedWebhookConnectionSchema = z.object({
  connection: ConnectionSchema,
  /** Shown exactly once. */
  hmacSecret: z.string(),
  bearerToken: z.string(),
});

/** Pilot credential connection (Aircall API ID/token, Calendly personal token, Telnyx API key). Validated server-side. */
export const CreateTokenConnectionSchema = z.object({
  provider: z.enum(['aircall', 'calendly', 'calcom', 'telnyx']),
  label: z.string().min(1).max(120),
  apiId: z.string().max(200).optional(),
  apiToken: z.string().min(10).max(4000),
});

export const UpdateEndpointSchema = z
  .object({ displayName: z.string().min(1).max(120), shared: z.boolean(), projectIds: z.array(Id).max(500), defaultProjectId: Id.nullable() })
  .partial();

// ───────────── Webhooks (outgoing) ─────────────

export const WebhookSubscriptionSchema = z.object({
  id: Id,
  url: z.string(),
  description: z.string().nullable(),
  events: z.array(WebhookEventTypeSchema),
  projectIds: z.array(Id),
  redactPii: z.boolean(),
  active: z.boolean(),
  secretRotatedAt: Iso.nullable(),
  createdAt: Iso,
});
export type WebhookSubscription = z.infer<typeof WebhookSubscriptionSchema>;

export const CreateWebhookSubscriptionSchema = z.object({
  url: z.url().max(2000),
  description: z.string().max(300).nullable().optional(),
  events: z.array(WebhookEventTypeSchema).min(1),
  projectIds: z.array(Id).max(500).default([]),
  redactPii: z.boolean().default(false),
});
export const UpdateWebhookSubscriptionSchema = CreateWebhookSubscriptionSchema.partial().extend({ active: z.boolean().optional() });
export const WebhookSecretSchema = z.object({ subscription: WebhookSubscriptionSchema, secret: z.string() });

export const WebhookDeliverySchema = z.object({
  id: Id,
  subscriptionId: Id,
  eventId: z.string(),
  eventType: z.string(),
  status: WebhookDeliveryStatusSchema,
  attempts: z.number().int(),
  lastStatusCode: z.number().int().nullable(),
  lastDurationMs: z.number().int().nullable(),
  lastError: z.string().nullable(),
  nextAttemptAt: Iso.nullable(),
  deliveredAt: Iso.nullable(),
  createdAt: Iso,
});
export type WebhookDelivery = z.infer<typeof WebhookDeliverySchema>;

// ───────────── Incoming events ─────────────

export const EventReceiptSchema = z.object({
  id: Id,
  connectionId: Id,
  eventId: z.string(),
  type: z.string(),
  status: ReceiptStatusSchema,
  statusDetail: z.string().nullable(),
  trace: z.array(z.object({ at: Iso, step: z.string(), detail: z.string().nullable() })),
  receivedAt: Iso,
  processedAt: Iso.nullable(),
});
export type EventReceipt = z.infer<typeof EventReceiptSchema>;

// ───────────── Agents & prompts ─────────────

export const PromptVersionSchema = z.object({
  id: Id,
  version: z.number().int(),
  state: VersionStateSchema,
  content: AgentPromptContentSchema,
  changeNote: z.string().nullable(),
  authorName: z.string().nullable(),
  publishedAt: Iso.nullable(),
  createdAt: Iso,
});
export type PromptVersion = z.infer<typeof PromptVersionSchema>;

export const DeploymentSchema = z.object({
  target: z.string(),
  status: DeploymentStatusSchema,
  detail: z.string().nullable(),
  globalVersion: z.number().int().nullable(),
  toolVersion: z.number().int().nullable(),
  promptVersion: z.number().int().nullable(),
  updatedAt: Iso,
});

export const AgentConfigSchema = z.object({
  id: Id,
  projectId: Id.nullable(),
  name: z.string(),
  published: PromptVersionSchema.nullable(),
  draft: PromptVersionSchema.nullable(),
  versions: z.array(PromptVersionSchema.pick({ id: true, version: true, state: true, changeNote: true, authorName: true, publishedAt: true, createdAt: true })),
  deployments: z.array(DeploymentSchema),
});
export type AgentConfig = z.infer<typeof AgentConfigSchema>;

export const SaveAgentDraftSchema = z.object({ content: AgentPromptContentSchema, changeNote: z.string().max(500).nullable().optional() });
export const PublishSchema = z.object({ changeNote: z.string().min(3).max(500) });

// ───────────── Platform: global instructions ─────────────

export const GlobalReleaseSummarySchema = z.object({
  id: Id,
  version: z.number().int(),
  state: VersionStateSchema,
  changeNote: z.string().nullable(),
  authorName: z.string().nullable(),
  publishedAt: Iso.nullable(),
  createdAt: Iso,
  updatedAt: Iso,
});
export const GlobalPromptReleaseSchema = GlobalReleaseSummarySchema.extend({ content: GlobalPromptContentSchema });
export type GlobalPromptRelease = z.infer<typeof GlobalPromptReleaseSchema>;
export const ToolInstructionReleaseSchema = GlobalReleaseSummarySchema.extend({ content: ToolInstructionContentSchema });
export type ToolInstructionRelease = z.infer<typeof ToolInstructionReleaseSchema>;

export const GlobalPromptOverviewSchema = z.object({
  prompt: z.object({ published: GlobalPromptReleaseSchema.nullable(), draft: GlobalPromptReleaseSchema.nullable(), history: z.array(GlobalReleaseSummarySchema) }),
  tools: z.object({ published: ToolInstructionReleaseSchema.nullable(), draft: ToolInstructionReleaseSchema.nullable(), history: z.array(GlobalReleaseSummarySchema) }),
  pins: z.array(z.object({ workspaceId: Id, workspaceName: z.string(), version: z.number().int(), reason: z.string(), expiresAt: Iso.nullable(), createdAt: Iso })),
  /** Application status per provider target, aggregated across workspaces. */
  application: z.array(z.object({ status: DeploymentStatusSchema, count: z.number().int() })),
});
export const SaveGlobalPromptDraftSchema = z.object({ content: GlobalPromptContentSchema, changeNote: z.string().max(500).nullable().optional() });
export const SaveToolDraftSchema = z.object({ content: ToolInstructionContentSchema, changeNote: z.string().max(500).nullable().optional() });
export const RollbackSchema = z.object({ toVersion: z.number().int().min(1), changeNote: z.string().min(3).max(500) });
export const PinWorkspaceSchema = z.object({ workspaceId: Id, version: z.number().int().min(1), reason: z.string().min(3).max(300), expiresAt: Iso.nullable().optional() });

export const PromptDiffSchema = z.object({
  fields: z.array(z.object({ field: z.string(), published: z.string(), draft: z.string(), changed: z.boolean() })),
});

// ───────────── Contacts, conversations, calls, appointments (read models) ─────────────

export const LeadSchema = z.object({
  interestId: Id,
  contactId: Id,
  displayName: z.string().nullable(),
  phoneE164: z.string().nullable(),
  email: z.string().nullable(),
  projectId: Id,
  projectName: z.string(),
  stage: FunnelStageSchema,
  /** Category of the structured loss reason when the stage is Perdido. */
  lossReasonCategory: LossReasonCategorySchema.nullable(),
  qualification: z.record(z.string(), z.object({ value: z.unknown(), source: z.string().nullable(), confidence: z.number().nullable(), status: QualificationValueStatusSchema })),
  advisorName: z.string().nullable(),
  sourceSystem: z.string().nullable(),
  campaignId: z.string().nullable(),
  nextAction: z.string().nullable(),
  nextActionAt: Iso.nullable(),
  whatsappPermission: PermissionStatusSchema,
  phonePermission: PermissionStatusSchema,
  isTest: z.boolean(),
  updatedAt: Iso,
});
export type Lead = z.infer<typeof LeadSchema>;
export const LeadListQuerySchema = PageQuerySchema.extend({ projectId: Id.optional(), q: z.string().max(120).optional(), stage: FunnelStageInputSchema.optional() });

export const ConversationSummarySchema = z.object({
  id: Id,
  contactId: Id,
  contactName: z.string().nullable(),
  contactPhone: z.string().nullable(),
  endpointName: z.string(),
  mode: ConversationModeSchema,
  activeProjectName: z.string().nullable(),
  assignedName: z.string().nullable(),
  lastMessagePreview: z.string().nullable(),
  lastMessageAt: Iso.nullable(),
  unreadCount: z.number().int(),
  /** Free-form reply window open (24h since last inbound). null when unknown. */
  serviceWindowOpen: z.boolean().nullable(),
  humanNeeded: z.boolean(),
  /** Handoff state of the active project's opportunity (or the most restrictive of the contact's when none is active). */
  aiControl: OpportunityControlStateSchema,
});
export type ConversationSummary = z.infer<typeof ConversationSummarySchema>;
export const ConversationListQuerySchema = PageQuerySchema.extend({
  projectId: Id.optional(),
  mode: ConversationModeSchema.optional(),
  filter: z.enum(['all', 'unread', 'awaiting_reply', 'human_needed', 'delivery_failed']).default('all'),
});

/** WA-10: automatic understanding of a received image/document (libs/backend/src/media_understanding). Never verified by a person. */
export const ReceivedMediaDescriptionSchema = z.object({
  /** pending = queued; ready = description available; not_available = unsupported/LLM off/budget (an advisor reviews it); failed = model error. */
  status: z.enum(['pending', 'ready', 'not_available', 'failed']),
  description: z.string().nullable(),
  keyText: z.string().nullable(),
  uncertain: z.boolean(),
  /** Spanish reason when not ready (e.g. formato no admitido, IA no configurada). */
  detail: z.string().nullable(),
});
export type ReceivedMediaDescription = z.infer<typeof ReceivedMediaDescriptionSchema>;

// ───────── Voice notes (transcription never replaces `body`) ─────────

export const TRANSCRIPT_STATUSES = ['pending', 'ready', 'failed', 'not_configured'] as const;
export const TranscriptStatusSchema = z.enum(TRANSCRIPT_STATUSES);
export type TranscriptStatus = z.infer<typeof TranscriptStatusSchema>;
export const TRANSCRIPT_STATUS_LABELS: Record<TranscriptStatus, string> = {
  pending: 'Transcripción pendiente',
  ready: 'Transcripción lista',
  failed: 'La transcripción falló',
  not_configured: 'Transcripción no configurada',
};

export const VoiceNoteSchema = z.object({
  /** null = no transcription was requested for this audio (e.g. an app-origin echo). */
  transcriptStatus: TranscriptStatusSchema.nullable(),
  transcript: z.string().nullable(),
  /** marked = provider confidences were used to mark uncertain spans; unverified = no confidences ("sin verificar"). */
  confidence: z.enum(['marked', 'unverified']).nullable(),
  /** Character ranges of `transcript` the provider was unsure about (to be clarified with the contact). */
  uncertain: z.array(z.object({ start: z.number().int().min(0), end: z.number().int().min(0) })),
  /** Why the transcript is missing/failed (Spanish, user-facing). */
  detail: z.string().nullable(),
  durationSeconds: z.number().nullable(),
  /** The audio is stored privately and can be played by authorized members. */
  playable: z.boolean(),
});
export type VoiceNote = z.infer<typeof VoiceNoteSchema>;

export const MessageSchema = z.object({
  id: Id,
  direction: DirectionSchema,
  author: MessageAuthorSchema,
  authorName: z.string().nullable(),
  kind: z.string(),
  body: z.string().nullable(),
  status: DeliveryStatusSchema,
  failureReason: z.string().nullable(),
  fromBusinessApp: z.boolean(),
  /** Imported from the WhatsApp Business app chat history (coexistence); never triggered the AI. */
  importedFromHistory: z.boolean().optional(),
  internalNote: z.boolean(),
  projectName: z.string().nullable(),
  createdAt: Iso,
  /** Received image/document understanding (automatic, unverified); absent for other messages. */
  mediaDescription: ReceivedMediaDescriptionSchema.nullable().optional(),
  /** Voice notes / audio (PRD §9, WA-03, WA-10): playback + automatic transcript; absent for other messages. */
  voiceNote: VoiceNoteSchema.nullable().optional(),
});
export type Message = z.infer<typeof MessageSchema>;

export const ConversationDetailSchema = ConversationSummarySchema.extend({
  version: z.number().int(),
  /** Stored summary: structured JSON (parse with parseConversationSummary) or legacy plain text. */
  summary: z.string().nullable(),
  /** Readable rendering of `summary` for integrations and non-web consumers. */
  summaryText: z.string().nullable(),
  messages: z.array(MessageSchema),
  leads: z.array(LeadSchema),
  /** Human/AI control of each opportunity (contact + project) the member can see (spec §05.3, §05.23, §08.4). */
  opportunities: z.array(OpportunityControlSchema),
  /**
   * Shared orchestration (addendum §10): an advisor is on a call with this contact right now, so automated replies,
   * follow-ups and AI calls are paused. `reconciling`: no call events for hours; still paused until Aircall confirms the end.
   */
  humanCall: z
    .object({
      label: z.string(),
      since: Iso,
      state: z.enum(['active', 'reconciling']),
      /** Lock id, for the manual release of a stuck lock (manager+). */
      lockId: z.string().optional(),
    })
    .nullable()
    .optional(),
});
export type ConversationDetail = z.infer<typeof ConversationDetailSchema>;

export const ConversationActionSchema = z.object({ expectedVersion: z.number().int().optional() });
export const InternalNoteSchema = z.object({ body: z.string().min(1).max(4000) });

export const CallSchema = z.object({
  id: Id,
  contactId: Id,
  contactName: z.string().nullable(),
  projectName: z.string().nullable(),
  direction: DirectionSchema,
  status: CallStatusSchema,
  outcome: z.string().nullable(),
  startedAt: Iso.nullable(),
  endedAt: Iso.nullable(),
  durationSeconds: z.number().int().nullable(),
  analysisStatus: AnalysisStatusSchema,
  summary: z.string().nullable(),
  hasRecording: z.boolean(),
  createdAt: Iso,
});
export type Call = z.infer<typeof CallSchema>;

export const AppointmentSchema = z.object({
  id: Id,
  contactId: Id,
  contactName: z.string().nullable(),
  projectId: Id,
  projectName: z.string(),
  status: AppointmentStatusSchema,
  startAt: Iso,
  endAt: Iso.nullable(),
  timezone: z.string(),
  location: z.string().nullable(),
  advisorName: z.string().nullable(),
  connectionLabel: z.string(),
  rescheduleUrl: z.string().nullable(),
  cancelUrl: z.string().nullable(),
  /** Outcome reported by a human or synced from the CRM (spec §10.5); null = not reported yet. */
  outcome: AppointmentOutcomeSchema.nullable().default(null),
  outcomeSource: AppointmentOutcomeSourceSchema.nullable().default(null),
  outcomeAt: Iso.nullable().default(null),
  outcomeByName: z.string().nullable().default(null),
  /** «Resultado pendiente de confirmar»: the end-of-day deadline passed without an outcome (§10.5). */
  outcomePending: z.boolean().default(false),
  createdAt: Iso,
});
export type Appointment = z.infer<typeof AppointmentSchema>;
export const AppointmentListQuerySchema = PageQuerySchema.extend({
  projectId: Id.optional(),
  status: AppointmentStatusSchema.optional(),
  from: Iso.optional(),
  to: Iso.optional(),
});

export const UpdateHandoffSchema = z.object({
  status: z.enum(['acknowledged', 'resolved', 'cancelled']),
  note: z.string().max(1000).nullable().optional(),
});

export const HandoffSchema = z.object({
  id: Id,
  contactId: Id,
  contactName: z.string().nullable(),
  conversationId: Id.nullable(),
  reason: z.string(),
  recommendedNextStep: z.string().nullable(),
  status: HandoffStatusSchema,
  assignedName: z.string().nullable(),
  createdAt: Iso,
  projectName: z.string().nullable().optional(),
  assignmentSource: HandoffAssignmentSourceSchema.nullable().optional(),
  /** SLA deadline for a person to take the case. */
  dueAt: Iso.nullable().optional(),
  overdue: z.boolean().optional(),
  callbackPreference: z.string().nullable().optional(),
});
export type Handoff = z.infer<typeof HandoffSchema>;

// ───────────── Home ─────────────

export const HomeSchema = z.object({
  attention: z.array(
    z.object({
      kind: z.enum(['handoff', 'connection', 'appointment', 'delivery_failure', 'setup', 'sync_failure', 'usage']),
      title: z.string(),
      detail: z.string().nullable(),
      href: z.string(),
      at: Iso.nullable(),
    }),
  ),
  funnel: z.object({
    eligibleLeads: z.number().int().nullable(),
    contacted: z.number().int().nullable(),
    reached: z.number().int().nullable(),
    qualified: z.number().int().nullable(),
    confirmedAppointments: z.number().int().nullable(),
  }),
  upcomingAppointments: z.array(AppointmentSchema),
  pendingHandoffs: z.array(HandoffSchema),
  connections: z.array(z.object({ id: Id, provider: ConnectionProviderSchema, label: z.string(), status: ConnectionStatusSchema, healthMessage: z.string().nullable() })),
  setup: z.object({ ready: z.number().int(), total: z.number().int() }),
  aiStatus: z.object({ whatsapp: z.object({ openNow: z.boolean(), nextOpenAt: Iso.nullable(), configured: z.boolean() }), voice: z.object({ openNow: z.boolean(), nextOpenAt: Iso.nullable(), configured: z.boolean() }) }),
  /** v1 §10.9: open alerts (critical first) — project, reason, waiting time, who has it or «sin tomar». */
  alerts: z.array(CaseAlertSchema),
  /** Per-project summary (last 30 days): appointments booked by LIA, visits held, open alerts. */
  projects: z.array(HomeProjectSummarySchema),
});
export type Home = z.infer<typeof HomeSchema>;

// ───────────── Audit ─────────────

export const AuditEntrySchema = z.object({
  id: z.string(),
  action: z.string(),
  objectType: z.string(),
  objectId: z.string().nullable(),
  actorName: z.string().nullable(),
  metadata: z.record(z.string(), z.unknown()).nullable(),
  ip: z.string().nullable(),
  createdAt: Iso,
});
export const AuditPageSchema = pageOf(AuditEntrySchema);

// ───────────── Knowledge (listing only in Phase 1) ─────────────

export const DocumentSchema = z.object({
  id: Id,
  projectId: Id.nullable(),
  title: z.string(),
  kind: z.string(),
  status: DocumentStatusSchema,
  version: z.number().int(),
  updatedAt: Iso,
});

export const ChannelKindQuerySchema = z.object({ channel: ChannelKindSchema.optional(), projectId: Id.optional() });
